import React from 'react';
import {
    User,
    CreditCard,
    Car,
    MapPin,
    AlertTriangle,
    LogOut,
    X
} from 'lucide-react';
import type { Section, MenuItem } from '@/types';

interface SidebarProps {
    currentSection: Section;
    onSectionChange: (section: Section) => void;
    onLogout: () => void;
    isOpen: boolean;
    onClose: () => void;
    className?: string;
}

const menuItems: MenuItem[] = [
    { id: 'user', label: 'User', icon: User },
    { id: 'penalties', label: 'Penalties', icon: AlertTriangle },
    { id: 'parkings', label: 'Parkings', icon: MapPin },
    { id: 'cars', label: 'Cars', icon: Car },
    { id: 'credit-cards', label: 'Credit Cards', icon: CreditCard },
];

export const Sidebar: React.FC<SidebarProps> = ({
                                                    currentSection,
                                                    onSectionChange,
                                                    onLogout,
                                                    isOpen,
                                                    onClose,
                                                    className = '',
                                                }) => {
    const handleSectionClick = (section: Section) => {
        onSectionChange(section);
        onClose(); // Close mobile sidebar
    };

    return (
        <>
            {/* Desktop Sidebar */}
            <div className={`hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 ${className}`}>
                <div className="flex flex-col flex-grow bg-white border-r border-gray-200 overflow-y-auto">
                    <div className="flex items-center flex-shrink-0 px-6 py-4 border-b border-gray-200">
                        <h1 className="text-xl font-bold text-gray-900">
                            User Viewer
                        </h1>
                    </div>

                    <nav className="flex-1 px-4 py-4 space-y-1">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = currentSection === item.id;

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleSectionClick(item.id)}
                                    className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        isActive
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                >
                                    <Icon
                                        className={`mr-3 flex-shrink-0 h-5 w-5 ${
                                            isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                                        }`}
                                    />
                                    {item.label}
                                    {item.count !== undefined && (
                                        <span className={`ml-auto inline-block py-0.5 px-2 text-xs rounded-full ${
                                            isActive ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'
                                        }`}>
                      {item.count}
                    </span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="flex-shrink-0 border-t border-gray-200 p-4">
                        <button
                            onClick={onLogout}
                            className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md text-red-600 hover:text-red-900 hover:bg-red-50 transition-colors"
                        >
                            <LogOut className="mr-3 flex-shrink-0 h-5 w-5 text-red-400 group-hover:text-red-500" />
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Sidebar */}
            <div className={`lg:hidden fixed inset-0 flex z-40 ${isOpen ? '' : 'pointer-events-none'}`}>
                {/* Overlay */}
                <div
                    className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ${
                        isOpen ? 'opacity-100' : 'opacity-0'
                    }`}
                    onClick={onClose}
                />

                {/* Sidebar */}
                <div
                    className={`relative flex-1 flex flex-col max-w-xs w-full bg-white transform transition-transform ${
                        isOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                >
                    {/* Close button */}
                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                        <button
                            type="button"
                            className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                            onClick={onClose}
                        >
                            <X className="h-6 w-6 text-white" />
                        </button>
                    </div>

                    {/* Sidebar content */}
                    <div className="flex-1 h-0 overflow-y-auto">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h1 className="text-xl font-bold text-gray-900">
                                User Viewer
                            </h1>
                        </div>

                        <nav className="px-4 py-4 space-y-1">
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = currentSection === item.id;

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleSectionClick(item.id)}
                                        className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                            isActive
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                    >
                                        <Icon
                                            className={`mr-3 flex-shrink-0 h-5 w-5 ${
                                                isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                                            }`}
                                        />
                                        {item.label}
                                        {item.count !== undefined && (
                                            <span className={`ml-auto inline-block py-0.5 px-2 text-xs rounded-full ${
                                                isActive ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'
                                            }`}>
                        {item.count}
                      </span>
                                        )}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 p-4">
                        <button
                            onClick={onLogout}
                            className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md text-red-600 hover:text-red-900 hover:bg-red-50 transition-colors"
                        >
                            <LogOut className="mr-3 flex-shrink-0 h-5 w-5 text-red-400 group-hover:text-red-500" />
                            Logout
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
